// Item-data lookups that tolerate missing or partial entries in src/data/items.ts (owner: loot).
// Pure: no Phaser import (used by unit tests).

import { EQUIPMENT, MAJOR_UPGRADES, MODS, MUTATIONS, SYNERGIES, WEAPONS } from '../../data/items';
import type {
  EquipDef,
  EquipId,
  MajorUpgradeDef,
  MajorUpgradeId,
  ModId,
  MutationDef,
  MutationId,
  SynergyDef,
  SynergyId,
  WeaponDef,
  WeaponId,
  WeaponModifier,
} from '../../core/types';
import {
  FALLBACK_MODS,
  FALLBACK_SYNERGIES,
  FALLBACK_WEAPONS,
  MUTATION_FLAGS,
  SYNERGY_FLAGS,
  UPGRADE_FLAGS,
} from './defaults';

function lookup<K extends string, V>(table: unknown, id: K): V | undefined {
  try {
    return (table as Partial<Record<K, V>> | undefined)?.[id];
  } catch {
    return undefined;
  }
}

/** Weapon definition: data entry, else the combat fallback, else the pulse carbine. */
export function weaponDef(id: WeaponId): WeaponDef {
  const d = lookup<WeaponId, WeaponDef>(WEAPONS, id);
  if (d && d.behavior && d.projectile) return d;
  return FALLBACK_WEAPONS[id] ?? FALLBACK_WEAPONS.pulse_carbine;
}

export function modModifier(id: ModId): WeaponModifier | null {
  const d = lookup<ModId, { weapon?: WeaponModifier }>(MODS, id);
  return d?.weapon ?? FALLBACK_MODS[id] ?? null;
}

export function equipDef(id: EquipId): EquipDef | null {
  return lookup<EquipId, EquipDef>(EQUIPMENT, id) ?? null;
}

export function mutationDef(id: MutationId): MutationDef | null {
  return lookup<MutationId, MutationDef>(MUTATIONS, id) ?? null;
}

export function synergyDef(id: SynergyId): SynergyDef | null {
  return lookup<SynergyId, SynergyDef>(SYNERGIES, id) ?? FALLBACK_SYNERGIES[id] ?? null;
}

export function upgradeDef(id: MajorUpgradeId): MajorUpgradeDef | null {
  return lookup<MajorUpgradeId, MajorUpgradeDef>(MAJOR_UPGRADES, id) ?? null;
}

/** Flags a mutation grants: data flags ∪ the ARCHITECTURE flag table. */
export function mutationFlags(id: MutationId): string[] {
  return union(mutationDef(id)?.flags, MUTATION_FLAGS[id]);
}

export function synergyFlags(id: SynergyId): string[] {
  return union(synergyDef(id)?.flags, SYNERGY_FLAGS[id]);
}

export function upgradeFlags(id: MajorUpgradeId): string[] {
  return union(upgradeDef(id)?.flags, UPGRADE_FLAGS[id]);
}

function union(a: readonly string[] | undefined, b: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const f of a ?? []) if (!out.includes(f)) out.push(f);
  for (const f of b ?? []) if (!out.includes(f)) out.push(f);
  return out;
}
